import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useQuery, UserTopFollowersDocument } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogError';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UserTopFollowers($id: UUID!) {
    userTopFollowers(id: $id) {
      id
      username
      publicAddress
      webappUri
      avatar {
        id
        url
      }
    }
  }
`);

const AVATAR_SIZE = 24;
export const SocialProof = ({ userId, loading }: { userId: string; loading?: boolean }) => {
  const { data, isLoading, error } = useQuery(UserTopFollowersDocument, {
    variables: {
      id: userId,
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const logError = useLogError();

  React.useEffect(() => {
    if (!error) return;

    logError({
      error,
    });
  }, [error, logError]);

  const users = React.useMemo(() => data?.data.userTopFollowers ?? [], [data]);

  const followerCount = users.length;

  const userNames = React.useMemo(
    () =>
      users.map(u => {
        return {
          id: u.id,
          name: u.username,
          publicAddress: u.publicAddress,
          webappUri: u.webappUri,
        };
      }),
    [users],
  );

  if (isLoading || loading) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex flex-row-reverse">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`social-proof-avatar-${index}`}
              className="-mr-3.5 h-6 w-6 animate-pulse rounded-full bg-neutral200 sm:-mr-3 "
            />
          ))}
        </div>
        <div className="h-3.5 w-48 animate-pulse rounded-md bg-neutral200 py-1 md:w-56" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-row [&>div:nth-child(1)]:z-above2 [&>div:nth-child(2)]:z-above1 [&>div:nth-child(2)]:-ml-3.5  sm:[&>div:nth-child(2)]:-ml-3  [&>div:nth-child(3)]:-ml-3.5  sm:[&>div:nth-child(3)]:-ml-3">
        {users.map(u => {
          return (
            <div key={u.id} className="h-6 w-6">
              <Avatar
                src={u.avatar?.url}
                size={AVATAR_SIZE}
                username={u.username}
                walletAddress={u.publicAddress}
                hasShadow={false}
                hasBorder
                borderRadius="50%"
                webappUri={u.webappUri}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center overflow-hidden whitespace-nowrap font-base text-base-s font-medium text-base500">
        Followed by&nbsp;
        {userNames.map((u, i) => {
          const moreThanOne = followerCount > 1;
          const isLast = i === followerCount - 1;
          const userNameIsPublicAddress =
            u.name.substring(0, 5) === u.publicAddress.substring(0, 5);

          return (
            <div key={`link-${u.id}`} className="inline-flex items-center overflow-hidden">
              {isLast && moreThanOne && <>and&nbsp;</>}

              <AccessibleLink
                className={clsx(
                  'overflow-hidden font-semibold text-base700 hover:text-black',
                  moreThanOne && 'max-w-[84px] sm:max-w-[140px]',
                  //this is to show only first 5 char if username is public address before ellipsis in mobile
                  userNameIsPublicAddress && '!max-w-[58px] sm:!max-w-[140px]',
                )}
                pathnameUri={webappUriPath(u.webappUri)}
              >
                <p className="hidden overflow-hidden text-ellipsis whitespace-nowrap md:block">
                  {u.name}
                </p>
                {/* We do this to avoid mobile overflow with multiple ellipsis */}
                <p className="overflow-hidden text-ellipsis whitespace-nowrap md:hidden">
                  {userNameIsPublicAddress ? u.publicAddress : u.name}
                </p>
              </AccessibleLink>
              {!isLast && followerCount !== 2 ? <>,&nbsp;</> : <>&nbsp;</>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
